import { useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { routes } from 'routes/routes';
import type {
  CheckMagicLinkLocationState,
  ForgotPasswordLocationState,
  LoginLocationState,
  LoginSignpostLocationState,
  LoginWithCodeLocationState,
  PasswordlessLocationState,
  SignUpLocationState,
} from 'pages';
import type { OnboardingLocationState } from '../types';

export const useOnboardingNavigation = <T extends OnboardingLocationState>() => {
  const location = useLocation();
  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment -- TODO: disabled by automatic tool, need to check if it could be fixed or stay ignored in some rare cases
  const state: T | undefined = location.state;
  const navigateToSignUp = useNavigate();
  const navigateToPasswordLogin = useNavigate();
  const navigateToPasswordless = useNavigate();
  const navigateToLogin = useNavigate();
  const navigateToWithMagicLink = useNavigate();
  const navigateToForgotPassword = useNavigate();
  const navigateToCheckCode = useNavigate();

  return useMemo(
    () => ({
      async onNavigateToCheckCode({
        referrer,
        tokenToGetCode,
        tokenToLogin,
      }: {
        referrer?: string;
        tokenToGetCode?: string;
        tokenToLogin?: string;
      }) {
        await navigateToCheckCode(routes.login.code, {
          state: {
            ...state,
            referrer: referrer ?? state?.referrer,
            tokenToGetCode,
            tokenToLogin,
          } satisfies LoginWithCodeLocationState,
        });
      },
      async onNavigateToForgotPassword(email?: string) {
        await navigateToForgotPassword('/forgot-password/', {
          state: { ...state, email } satisfies ForgotPasswordLocationState,
        });
      },
      async onNavigateToPasswordlessLogin(loginState?: PasswordlessLocationState) {
        await navigateToPasswordless('/login/magic-link', {
          state: { ...state, ...loginState } satisfies CheckMagicLinkLocationState,
        });
      },
      async onNavigateToPasswordLogin(loginState?: LoginLocationState) {
        await navigateToPasswordLogin('/login/password', {
          state: { ...state, ...loginState } satisfies LoginLocationState,
        });
      },
      async onNavigateToPrimaryLogin(loginState?: LoginSignpostLocationState) {
        await navigateToLogin(routes.login.index, {
          state: { ...state, ...loginState } satisfies LoginSignpostLocationState,
        });
      },
      async onNavigateToSignUp(signUpState?: SignUpLocationState) {
        await navigateToSignUp(routes.signUp, { state: { ...state, ...signUpState } satisfies SignUpLocationState });
      },
      async onReplaceCheckMagicLink() {
        await navigateToWithMagicLink('/magic-link', { replace: true, state });
      },
      async onReplaceToLogin() {
        await navigateToLogin('/login/password', {
          replace: true,
          state: state satisfies LoginLocationState | undefined,
        });
      },
    }),
    [
      navigateToCheckCode,
      navigateToForgotPassword,
      navigateToLogin,
      navigateToPasswordLogin,
      navigateToPasswordless,
      navigateToSignUp,
      navigateToWithMagicLink,
      state,
    ],
  );
};
